import React from "react";
import { Link } from "react-router-dom";
import { Phone, MapPin, ShieldCheck, Heart, Hammer, Star } from "lucide-react";
import { Button } from "../components/ui/button";
import { COMPANY, HERO_IMAGE, VALUES_ICONS } from "../lib/constants";
import { useLang } from "../lib/i18n";

const ICONS = { Heart, Hammer, ShieldCheck, Star };

export default function AboutPage() {
  const { t } = useLang();

  return (
    <>
      <section className="section-y border-b border-border" data-testid="about-hero">
        <div className="container-x grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="overline">{t.about.overline}</span>
            <h1 className="mt-4 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              {t.about.title}
            </h1>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed">{t.about.p1}</p>
            <p className="mt-4 text-base text-muted-foreground leading-relaxed">
              {t.about.p2pre}
              <span className="font-semibold text-foreground">{t.about.p2bold}</span>
              {t.about.p2post}
              {COMPANY.cities.slice(1).join(", ")}.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href={COMPANY.phoneHref} data-testid="about-call-link">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2 rounded-full px-7 h-12 font-semibold w-full sm:w-auto">
                  <Phone size={18} /> {t.common.call} {COMPANY.phoneDisplay}
                </Button>
              </a>
              <Link to="/contact" data-testid="about-quote-link">
                <Button size="lg" variant="outline" className="rounded-full px-7 h-12 font-semibold border-2 w-full sm:w-auto">
                  {t.common.getFreeQuote}
                </Button>
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-2xl overflow-hidden border border-border shadow-2xl shadow-primary/10">
              <img src={HERO_IMAGE} alt={t.about.title} className="w-full h-[480px] object-cover" />
            </div>
            <div className="absolute -bottom-5 -right-5 sm:-bottom-7 sm:-right-7 bg-background border border-border rounded-xl shadow-xl p-4 max-w-[220px] hidden sm:flex items-center gap-3">
              <MapPin size={20} className="text-primary shrink-0" />
              <div className="text-sm leading-tight">
                <div className="font-semibold">{t.about.areaCity}</div>
                <div className="text-muted-foreground text-xs">{t.about.areaBadge}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-y" data-testid="about-values">
        <div className="container-x">
          <div className="max-w-2xl">
            <span className="overline">{t.about.valuesOverline}</span>
            <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              {t.about.valuesTitle}
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {t.about.values.map((v, i) => {
              const Icon = ICONS[VALUES_ICONS[i]] || Heart;
              return (
                <div
                  key={i}
                  className="rounded-2xl border border-border bg-card p-6 hover:-translate-y-1 hover:shadow-lg hover:border-primary/40 transition-all"
                  data-testid={`value-card-${i}`}
                >
                  <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary grid place-items-center">
                    <Icon size={20} strokeWidth={2.25} />
                  </div>
                  <h3 className="mt-5 font-display font-bold text-lg">{v.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
